import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { useState, useMemo, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed,
  Bot, User, Clock,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { SearchInput } from '@/components/molecules/SearchInput'
import { SortBar, type SortColumn } from '@/components/molecules/SortBar'
import { PaginationBar } from '@/components/molecules/PaginationBar'
import { ListEmptyState } from '@/components/molecules/ListEmptyState'
import { ListSkeleton } from '@/components/molecules/ListSkeleton'
import { StatChips } from '@/components/molecules/StatChips'
import { AudioPlayer } from '@/components/molecules/AudioPlayer'
import LiveCallPanel from '@/components/organisms/LiveCallPanel'
import { useUrlListState } from '@/hooks/useUrlListState'
import { listSearchSchema } from '@/lib/list-search-schema'
import {
  useCallsList, useCallStats, useCallDetail,
} from '@/hooks/usePhone'
import { phoneAPI } from '@/lib/api/phone'
import { phoneKeys } from '@/hooks/usePhone'
import { useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import type { CallListItem } from '@/lib/api/phone'
import { useTranslation } from 'react-i18next'

const callsSearchSchema = listSearchSchema.extend({
  status: z.string().optional(),
  direction: z.enum(['inbound', 'outbound']).optional(),
})

export const Route = createFileRoute('/_authenticated/phone/calls')({
  validateSearch: callsSearchSchema,
  component: CallsTab,
})

// ── Constants ──

const CALL_STATUS_STYLES: Record<string, string> = {
  completed: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  'in-progress': 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30',
  initiated: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30',
  ringing: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30',
  'no-answer': 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30',
  failed: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30',
  busy: 'bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30',
}

const DIRECTION_ICONS: Record<string, typeof PhoneIncoming> = {
  inbound: PhoneIncoming,
  outbound: PhoneOutgoing,
}

const CALL_SORT_COLUMNS: SortColumn[] = [
  { key: 'startedAt', label: 'Time' },
  { key: 'direction', label: 'Direction' },
  { key: 'fromNumber', label: 'From' },
  { key: 'toNumber', label: 'To' },
  { key: 'duration', label: 'Duration' },
  { key: 'status', label: 'Status' },
]

function fmtDuration(seconds: number | null): string {
  if (!seconds) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function fmtTime(d: string | null): string {
  if (!d) return '—'
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

// ── Stats Header ──

function StatsHeader({ from, to }: { from?: string; to?: string }) {
  const { t } = useTranslation()
  const { data: stats, isLoading } = useCallStats(from, to)

  if (isLoading || !stats) return null

  return (
    <StatChips
      items={[
        { label: t('phoneDashboard:totalCalls'), value: stats.total, icon: Phone, color: 'text-blue-500' },
        { label: t('phoneDashboard:inbound'), value: stats.inbound, icon: PhoneIncoming, color: 'text-green-500' },
        { label: t('phoneDashboard:outbound'), value: stats.outbound, icon: PhoneOutgoing, color: 'text-purple-500' },
        { label: t('phoneDashboard:aiHandled'), value: stats.aiCalls, icon: Bot, color: 'text-cyan-500' },
        { label: t('phoneDashboard:missed'), value: stats.missed, icon: PhoneMissed, color: 'text-red-500' },
        { label: t('phoneDashboard:avgDuration'), value: fmtDuration(stats.avgDuration), icon: Clock, color: 'text-amber-500' },
      ]}
    />
  )
}

// ── Main Component ──

function CallsTab() {
  const { t } = useTranslation()
  const list = useUrlListState({ from: '/_authenticated/phone/calls', defaultSort: 'startedAt' })
  const statusFilter = list.getFilter<string>('status') ?? ''
  const directionFilter = list.getFilter<string>('direction') ?? ''
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null)

  const params = useMemo(() => ({
    search: list.debouncedSearch || undefined,
    status: statusFilter || undefined,
    direction: directionFilter || undefined,
    page: list.page,
    perPage: list.perPage,
    sortBy: list.sortBy,
    sortDir: list.sortDir,
  }), [list.debouncedSearch, statusFilter, directionFilter, list.page, list.perPage, list.sortBy, list.sortDir])

  const { data, isLoading } = useCallsList(params)

  const statusOptions = [
    { key: '', label: t('phoneDashboard:all') },
    { key: 'completed', label: t('phoneDashboard:completed') },
    { key: 'in-progress', label: t('phoneDashboard:inProgress') },
    { key: 'no-answer', label: t('phoneDashboard:missedStatus') },
    { key: 'failed', label: t('phoneDashboard:failed') },
  ]

  const directionOptions = [
    { key: '', label: t('phoneDashboard:all') },
    { key: 'inbound', label: t('phoneDashboard:inbound') },
    { key: 'outbound', label: t('phoneDashboard:outbound') },
  ]

  return (
    <div className="space-y-4">
      <StatsHeader />
      <div className="flex flex-col sm:flex-row gap-3">
        <SearchInput value={list.search} onChange={list.setSearch} placeholder={t('phoneDashboard:searchCalls')} className="flex-1" />
        <div className="flex items-center gap-1">
          {statusOptions.map(opt => (
            <Button key={opt.key} size="sm" variant={statusFilter === opt.key ? 'default' : 'outline'}
              onClick={() => list.setFilter('status', opt.key || null)} className="text-xs">{opt.label}</Button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {directionOptions.map(opt => (
            <Button key={opt.key} size="sm" variant={directionFilter === opt.key ? 'default' : 'outline'}
              onClick={() => list.setFilter('direction', opt.key || null)} className="text-xs">{opt.label}</Button>
          ))}
        </div>
      </div>
      <SortBar columns={CALL_SORT_COLUMNS} sortBy={list.sortBy} sortDir={list.sortDir} onSort={list.handleSort} />
      {isLoading ? (
        <ListSkeleton rows={8} />
      ) : !data?.data?.length ? (
        <ListEmptyState icon={Phone} title={t('phoneDashboard:noCallsFound')} description={t('phoneDashboard:callsWillAppearHereOnceYourPhoneNumbersA')} />
      ) : (
        <div className="space-y-2">
          <AnimatePresence mode="popLayout">
            {data.data.map((call, i) => (
              <div key={call.id}>
                <CallRow call={call} index={i} selected={selectedCallId === call.id}
                  onSelect={() => setSelectedCallId(selectedCallId === call.id ? null : call.id)} />
                <AnimatePresence>
                  {selectedCallId === call.id && <CallDetailPanel callId={call.id} />}
                </AnimatePresence>
              </div>
            ))}
          </AnimatePresence>
        </div>
      )}
      {data?.pagination && (
        <PaginationBar page={data.pagination.page} totalPages={data.pagination.totalPages} total={data.pagination.total} perPage={data.pagination.perPage} onPage={list.setPage} onPerPage={list.setPerPage} />
      )}
    </div>
  )
}

// ── Call Row ──

function CallRow({ call, index, selected, onSelect }: { call: CallListItem; index: number; selected: boolean; onSelect: () => void }) {
  const DirIcon = DIRECTION_ICONS[call.direction] ?? Phone
  const TypeIcon = call.type.includes('ai') ? Bot : User
  const statusClass = CALL_STATUS_STYLES[call.status] ?? 'bg-muted text-muted-foreground border-border'

  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ delay: index * 0.02 }}
      onClick={onSelect}
      className={cn('group flex items-center gap-4 rounded-xl border bg-card/50 backdrop-blur-sm p-4 cursor-pointer transition-colors',
        selected ? 'border-primary bg-primary/5' : 'border-border/50 hover:border-border')}>
      <div className="flex items-center gap-2 shrink-0">
        <DirIcon className={cn('w-4 h-4', call.direction === 'inbound' ? 'text-green-500' : 'text-purple-500')} />
        <TypeIcon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{call.fromNumber}</span>
          <span className="text-muted-foreground">→</span>
          <span className="truncate">{call.toNumber}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
          <span>{fmtTime(call.startedAt)}</span>
          {call.duration && <span>· {fmtDuration(call.duration)}</span>}
          {call.type !== 'standard' && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{call.type.replace('_', ' ')}</Badge>}
        </div>
      </div>
      <Badge variant="outline" className={cn('shrink-0 text-xs', statusClass)}>{call.status}</Badge>
    </motion.div>
  )
}

// ── Call Detail Panel ──

function CallDetailPanel({ callId }: { callId: string }) {
  const { t } = useTranslation()
  const { data: detail, isLoading } = useCallDetail(callId)
  const [recState, setRecState] = useState<'idle' | 'fetching' | 'done' | 'none'>('idle')
  const [recUrl, setRecUrl] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const fetchedRef = useRef(false)

  const effectiveRecUrl = recUrl || detail?.recordingUrl || null

  useEffect(() => {
    if (!detail || effectiveRecUrl || fetchedRef.current) return
    if (detail.status !== 'completed' || !detail.twilioCallSid) {
      setRecState('none')
      return
    }
    fetchedRef.current = true
    setRecState('fetching')
    phoneAPI.calls.fetchRecording(detail.id)
      .then(result => {
        if (result.recordingUrl) {
          setRecUrl(result.recordingUrl)
          setRecState('done')
          void queryClient.invalidateQueries({ queryKey: phoneKeys.callDetail(callId) })
        } else {
          setRecState('none')
        }
      })
      .catch(() => setRecState('none'))
  }, [detail, effectiveRecUrl, callId, queryClient])

  useEffect(() => {
    fetchedRef.current = false
    setRecUrl(null)
    setRecState('idle')
  }, [callId])

  if (isLoading) return <div className="p-4 text-center text-muted-foreground text-sm">{t('phoneDashboard:loadingCallDetails')}</div>
  if (!detail) return null

  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
      className="rounded-xl border border-primary/20 bg-card p-4 space-y-4">
      {effectiveRecUrl ? (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
          <AudioPlayer src={effectiveRecUrl} title={t('phoneDashboard:callRecording')} />
        </motion.div>
      ) : recState === 'fetching' ? (
        <div className="flex items-center gap-2 py-2">
          <motion.div
            className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
          <span className="text-xs text-muted-foreground">{t('phoneDashboard:fetchingRecording')}</span>
        </div>
      ) : null}

      {detail.transcriptions && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">{t('phoneDashboard:transcription')}</p>
          <p className="text-sm bg-muted/30 rounded-lg p-3 whitespace-pre-wrap">{detail.transcriptions}</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div><span className="text-muted-foreground">{t('phoneDashboard:direction')}</span><p className="font-medium">{detail.direction}</p></div>
        <div><span className="text-muted-foreground">{t('phoneDashboard:status')}</span><p className="font-medium">{detail.status}</p></div>
        <div><span className="text-muted-foreground">{t('phoneDashboard:duration')}</span><p className="font-medium">{fmtDuration(detail.duration)}</p></div>
        <div><span className="text-muted-foreground">{t('phoneDashboard:type')}</span><p className="font-medium">{detail.type}</p></div>
        {detail.twilioCallSid && <div className="col-span-2"><span className="text-muted-foreground">{t('phoneDashboard:twilioSid')}</span><p className="font-mono text-[10px]">{detail.twilioCallSid}</p></div>}
        {detail.callerId && <div><span className="text-muted-foreground">{t('phoneDashboard:callerId')}</span><p className="font-medium">{detail.callerId}</p></div>}
      </div>

      {detail.events && detail.events.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">{t('phoneDashboard:events', { count: detail.events.length })}</p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {detail.events.map((evt: any) => (
              <div key={evt.id} className="flex items-center gap-2 text-xs py-1 border-b border-border/30 last:border-0">
                <Badge variant="secondary" className="text-[9px] px-1.5 py-0 shrink-0">{evt.eventType.replace(/_/g, ' ')}</Badge>
                <span className="text-muted-foreground">{fmtTime(evt.createdAt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {detail.intelligence && detail.intelligence.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">{t('phoneDashboard:intelligence')}</p>
          {detail.intelligence.map((intel: any) => (
            <div key={intel.id} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
              <Bot className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
              <span className="font-medium">{intel.operatorName ?? intel.operatorType}</span>
              {intel.score != null && <span className="text-muted-foreground">Score: {(intel.score * 100).toFixed(0)}%</span>}
            </div>
          ))}
        </div>
      )}

      <LiveCallPanel
        callId={callId}
        isActive={detail.status === 'in-progress' || detail.status === 'ringing'}
      />
    </motion.div>
  )
}
